import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  type ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { LucideArrowUp, LucideSquare } from '@lucide/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { PmButtonComponent } from '@libs/ui';
import { ChatStateService } from '../../services/chat-state.service';

const MAX_HEIGHT_PX = 120;

@Component({
  selector: 'pm-chat-composer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex min-h-24 flex-col rounded-xl border border-border-subdued bg-surface p-3 transition-colors focus-within:outline focus-within:outline-[3px] focus-within:outline-form-input-outline-focus"
    >
      <textarea
        #textarea
        class="block max-h-[120px] w-full resize-none overflow-y-auto border-0 bg-transparent text-sm font-normal leading-[1.3] text-content-title outline-none [scrollbar-color:var(--border-color)_transparent] [scrollbar-width:thin] placeholder:text-content-subdued disabled:cursor-not-allowed disabled:text-form-input-content-disabled [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
        rows="1"
        [value]="text()"
        [disabled]="disabled()"
        [attr.placeholder]="placeholder() | translate"
        [attr.aria-label]="'chat.header.title' | translate"
        (input)="onInput($event)"
        (keydown)="onKeydown($event)"
      ></textarea>

      <div
        class="mt-auto flex h-8 flex-shrink-0 items-center justify-end pt-2 [&_pm-button_svg]:size-5"
      >
        @if (chat.isStreaming()) {
          <pm-button
            variant="primary"
            size="normal"
            [icon]="LucideSquare"
            [ariaLabel]="'chat.composer.stop'"
            (handleClick)="chat.stop()"
          />
        } @else if (canSend()) {
          <pm-button
            variant="primary"
            size="normal"
            [icon]="LucideArrowUp"
            [ariaLabel]="'chat.composer.send'"
            (handleClick)="send()"
          />
        }
      </div>
    </div>
  `,
  imports: [TranslatePipe, PmButtonComponent],
})
export class ChatComposerComponent {
  protected readonly chat = inject(ChatStateService);

  protected readonly LucideArrowUp = LucideArrowUp;
  protected readonly LucideSquare = LucideSquare;

  readonly disabled = input(false);

  protected readonly text = signal('');
  private readonly textarea =
    viewChild<ElementRef<HTMLTextAreaElement>>('textarea');

  protected readonly canSend = computed(
    () => !this.disabled() && this.text().trim().length > 0,
  );
  protected readonly placeholder = computed(() => {
    if (this.disabled()) {
      return 'chat.composer.unavailable';
    }
    return this.chat.hasConversation()
      ? 'chat.composer.placeholderMid'
      : 'chat.composer.placeholderStart';
  });

  constructor() {
    effect(() => {
      if (this.chat.isOpen() && !this.disabled()) {
        this.textarea()?.nativeElement.focus();
      }
    });
  }

  protected onInput(event: Event): void {
    const element = event.target as HTMLTextAreaElement;
    this.text.set(element.value);
    this.autoGrow(element);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  protected send(): void {
    if (!this.canSend()) {
      return;
    }
    this.chat.submit(this.text());
    this.text.set('');
    this.resetHeight();
  }

  private autoGrow(element: HTMLTextAreaElement): void {
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT_PX)}px`;
  }

  private resetHeight(): void {
    const element = this.textarea()?.nativeElement;
    if (element) {
      element.style.height = 'auto';
    }
  }
}
